import { __awaiter } from 'tslib';
import * as Long from 'long';
import { im_proto } from '../../proto/index.js';
import { ErrorType, InitResult, IMEvent, SdkConsts } from '../consts.js';
import { BaseError } from '../errors/BaseError.js';
import { Logger } from '../metrics/logger.js';
import { Conversation } from '../model/Conversation.js';
import { ConversationCoreInfo } from '../model/ConversationCoreInfo.js';
import { ConversationSettingInfo } from '../model/ConversationSettingInfo.js';
import { combineToArray, groupBy, chunkArray } from '../utils/tools.js';
import { debounce } from '../utils/trigger.js';
import { ContextClassBase, ServiceIdentity } from './RuntimeContext.js';

class ConversationManager extends ContextClassBase {
    constructor(ctx) {
        super(ctx);
        this.refreshBuffer = [];
        this.refreshDebounce = debounce(() => {
            this.triggerRefresh();
        }, 1000, true);
        this.conversations = new Map();
    }
    applyLocal(local) {
        local.forEach(c => {
            this.conversations.set(c.id, c);
        });
    }
    clearAll() {
        this.conversations.clear();
    }
    getWithCreateLocal(id, shortId, type, inboxType) {
        let localConv = this.getRaw(id);
        if (!localConv) {
            const newConv = new Conversation(this.ctx);
            newConv.coreInfo = new ConversationCoreInfo(newConv);
            newConv.settingInfo = new ConversationSettingInfo(newConv);
            newConv.id = id;
            newConv.shortId = shortId;
            newConv.type = type;
            newConv.isOffline = true;
            localConv = newConv;
            if (inboxType !== undefined) {
                newConv.coreInfo.inboxType = inboxType;
            }
            else if (inboxType === undefined &&
                type === im_proto.ConversationType.ONE_TO_ONE_CHAT &&
                id.split(':').length === 4) {
                // try to parse from conversation id
                newConv.coreInfo.inboxType = Number.parseInt(id.split(':')[0], 10);
            }
            Logger.ctxDebug(this.ctx, 'create local conv:', newConv);
            this.upsert(localConv);
        }
        return localConv;
    }
    upsert(c) {
        var _a;
        if (c === undefined) {
            throw new BaseError({
                ctx: this.ctx,
                msg: 'upsert undefined conv',
                type: ErrorType.InvalidParam,
                sender: this
            });
        }
        if (!this.resolve(ServiceIdentity.InboxType).isValidInbox(c.inboxType)) {
            throw new BaseError({
                ctx: this.ctx,
                msg: `invalid inbox: ${c.inboxType}`,
                type: ErrorType.InvalidParam,
                sender: this
            });
        }
        let local = this.conversations.get(c.id);
        if (local && c.isOffline) {
            Logger.ctxDebug(this.ctx, `local exist, try to use offline upsert, ignore, short id: ${local.shortId} with offline:${c.shortId}`);
            if (!local.isOffline) {
                Logger.ctxDebug(this.ctx, 'local is online, ignore update');
                return local;
            }
        }
        if (local) {
            Logger.ctxDebug(this.ctx, `merge conversation local=${local.version.toString()}:`, local, `new=${c.version.toString()}:`, c);
            const oldCore = local.coreInfo || new ConversationCoreInfo(local);
            const oldSetting = local.settingInfo || new ConversationSettingInfo(local);
            local = Object.assign(local, c);
            local.coreInfo = oldCore.mergeCore(c.coreInfo);
            local.settingInfo = oldSetting.mergeSetting(c.settingInfo);
            local.forceRefreshUnreadCount();
        }
        this.conversations.set(c.id, local !== null && local !== void 0 ? local : c);
        void ((_a = this.resolve(ServiceIdentity.DbProxy)) === null || _a === void 0 ? void 0 : _a.upsertConversation(local !== null && local !== void 0 ? local : c));
        if (this.ctx.initResult === InitResult.Succeeded) {
            this.resolve(ServiceIdentity.EventBus).emit(IMEvent.ConversationUpsert, this, local !== null && local !== void 0 ? local : c);
            this.resolve(ServiceIdentity.EventBus).emitEmpty(IMEvent.ConversationChange, this);
        }
        return c;
    }
    // 在以下几个时机调用
    // - 主动拉会话 (createConversation / getConversationInfo / ...)
    // - 收到消息
    // - 初始化结束 done
    // 分 batch 进行
    refreshLocalAsync() {
        return __awaiter(this, void 0, void 0, function* () {
            const allTempConvs = this.getConversationArray().filter(c => c.isOffline);
            if (allTempConvs.length === 0) {
                return;
            }
            return this.refreshAsync(allTempConvs);
        });
    }
    refreshLocal() {
        const allTempConvs = this.getConversationArray().filter(c => c.isOffline);
        if (allTempConvs.length === 0) {
            return;
        }
        this.refresh(allTempConvs);
    }
    triggerRefresh() {
        if (this.refreshBuffer.length === 0) {
            return;
        }
        Logger.ctxDebug(this.ctx, 'clear refresh buffer:', this.refreshBuffer);
        const toRefresh = [...this.refreshBuffer];
        this.refreshBuffer = [];
        void this.refreshAsync(toRefresh);
    }
    refresh(toRefresh) {
        var _a;
        const toAdded = combineToArray(toRefresh);
        const toPush = [];
        if (this.refreshBuffer.length === 0) {
            toPush.push(...toAdded);
        }
        else {
            // create a hashmap to store all ids
            const bufferAllConvs = {};
            this.refreshBuffer.forEach(c => (bufferAllConvs[c.id] = true));
            toAdded.forEach(t => {
                if (!bufferAllConvs[t.id]) {
                    // if not exist, add to refresh buffer
                    toPush.push(t);
                }
            });
        }
        if (toPush.length > 0) {
            this.refreshBuffer.push(...toPush);
        }
        const bufferCount = (_a = this.ctx.option.conversationRefreshCount) !== null && _a !== void 0 ? _a : SdkConsts.conversationRefreshCount;
        if (this.refreshBuffer.length >= bufferCount) {
            // batch size * 2
            this.triggerRefresh(); // trigger immidiately
        }
        else {
            this.refreshDebounce();
        }
    }
    refreshAsync(toRefresh) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            const allConvs = (_a = combineToArray(toRefresh)) !== null && _a !== void 0 ? _a : this.refreshBuffer;
            if (allConvs.length === 0) {
                return [];
            }
            Logger.ctxDebug(this.ctx, 'refresh conv: ', allConvs);
            const newConv = [];
            const groupByInbox = groupBy(allConvs, c => c.inboxType.toString());
            for (const inbox of Object.keys(groupByInbox)) {
                const reqBatch = chunkArray(groupByInbox[inbox], 5);
                for (const batch of reqBatch) {
                    try {
                        const response = yield this.resolve(ServiceIdentity.CoreApi).GetConversationInfoListV2({
                            conversations: batch.map(local => ({
                                conversationId: local.id,
                                conversationShortId: Long.fromString(local.shortId),
                                conversationType: local.type
                            })),
                            inboxType: Number.parseInt(inbox, 10)
                        });
                        const online = (_b = response === null || response === void 0 ? void 0 : response.body) === null || _b === void 0 ? void 0 : _b.get_conversation_info_list_v2_body;
                        const result = online.conversation_info_list;
                        // 这里要检查会话是否被返回了, 如果没返回就 delete local conv (可能被解散了导致拉不回来)
                        batch.forEach(localConv => {
                            // 如果结果中没有返回这个本地会话
                            if (result.filter(onlineConv => onlineConv.conversation_id === localConv.id).length === 0) {
                                // 删除这个本地会话
                                Logger.ctxDebug(this.ctx, 'delete local conv, may not exist online: ', localConv);
                                this.delete(localConv.id);
                            }
                        });
                        result.forEach(r => {
                            const c = this.upsert(Conversation.fromServerConversation(this.ctx, r, response === null || response === void 0 ? void 0 : response.log_id));
                            newConv.push(c);
                        });
                    }
                    catch (err) {
                        // 删除不存在的会话
                        if (err && (err === null || err === void 0 ? void 0 : err.type) === ErrorType.InternalError && ((_c = err === null || err === void 0 ? void 0 : err.message) === null || _c === void 0 ? void 0 : _c.includes('request.MGet empty'))) {
                            batch.forEach(localConv => {
                                Logger.ctxDebug(this.ctx, 'delete local conv, may not exist online: ', localConv);
                                this.delete(localConv.id);
                            });
                        }
                        else {
                            Logger.ctxError(this.ctx, 'unknown refresh error:', err);
                            // TODO: we need to refresh them later, if we met network error
                            // this.refreshBuffer.push(...batch)
                        }
                    }
                }
            }
            return newConv;
        });
    }
    get(conversationId) {
        const conv = this.getRaw(conversationId);
        if (!conv) {
            throw new BaseError({
                ctx: this.ctx,
                msg: `conversation ${conversationId} not exist in local`,
                type: ErrorType.ConversationNotExist,
                sender: this,
                args: {
                    conversationId
                }
            });
        }
        return conv;
    }
    getWithShortIdRaw(shortId) {
        for (const conv of this.conversations.values()) {
            if (conv.shortId === shortId) {
                return conv;
            }
        }
        return null;
    }
    getWithOnline(conversationId, conversationShortId, conversationType) {
        return __awaiter(this, void 0, void 0, function* () {
            let conv = this.getRaw(conversationId);
            if (!conv || conv.isOffline) {
                if (conversationShortId && conversationType) {
                    conv = this.getWithCreateLocal(conversationId, conversationShortId, conversationType);
                }
                else {
                    throw new BaseError({
                        ctx: this.ctx,
                        type: ErrorType.InvalidParam,
                        msg: 'no shortId and type provided',
                        sender: this
                    });
                }
            }
            yield this.refreshAsync(conv);
            return this.get(conversationId);
        });
    }
    getRaw(conversationId) {
        return this.conversations.get(conversationId);
    }
    // 默认的filter是过滤掉所有的陌生人+空会话, 可以自己传一个其他的filter
    getConversationArray(filter = ConversationManager.conversationFilter) {
        return Array.from(this.conversations.values())
            .filter(filter)
            .map(c => ({
            conv: c,
            rankScore: c.rankScore // TODO: optimize this by creating a sorted map
        }))
            .sort(ConversationManager.conversationComparator)
            .map(t => t.conv);
    }
    withConversation(conversationId) {
        return f => {
            const conversation = this.get(conversationId);
            return f(conversation);
        };
    }
    delete(conversationId, keepMessage = false) {
        var _a;
        const conv = this.getRaw(conversationId);
        if (!conv) {
            return;
        }
        if (!keepMessage) {
            this.getContext().resolve(ServiceIdentity.MessageManager).clearConversation(conversationId);
        }
        this.conversations.delete(conversationId);
        void ((_a = this.resolve(ServiceIdentity.DbProxy)) === null || _a === void 0 ? void 0 : _a.deleteConversation(conv));
        this.resolve(ServiceIdentity.EventBus).emitEmpty(IMEvent.ConversationChange, this);
        this.resolve(ServiceIdentity.EventBus).emit(IMEvent.ConversationDelete, this, conv);
    }
    markRead(conversationId, readIndex) {
        const conv = this.get(conversationId);
        if (conv.settingInfo.readIndex.gt(readIndex)) {
            return conv;
        }
        conv.settingInfo.readIndex = readIndex;
        return this.upsert(conv);
    }
    leave(conversationId) {
        var _a;
        const conv = this.get(conversationId);
        if (!conv) {
            return;
        }
        conv.isMember = false;
        void ((_a = this.resolve(ServiceIdentity.DbProxy)) === null || _a === void 0 ? void 0 : _a.upsertConversation(conv));
        this.resolve(ServiceIdentity.EventBus).emitEmpty(IMEvent.ConversationChange, this);
        this.resolve(ServiceIdentity.EventBus).emit(IMEvent.ConversationLeave, this, conv);
    }
    refreshTicket(conversationId) {
        return __awaiter(this, void 0, void 0, function* () {
            const conv = this.get(conversationId);
            const newTicket = yield this.resolve(ServiceIdentity.CoreApi).GetTicket({
                conversationType: conv.type,
                shortId: Long.fromString(conv.shortId),
                toId: Long.fromString(conv.type === im_proto.ConversationType.ONE_TO_ONE_CHAT ? conv.toParticipantUserId : conv.shortId),
                inboxType: conv.inboxType
            });
            conv.ticket = newTicket === null || newTicket === void 0 ? void 0 : newTicket.ticket;
            this.upsert(conv);
            Logger.ctxDebug(this.ctx, 'refresh ticket for conv:', conv);
        });
    }
    static conversationFilter(c) {
        return c.mode === 0 && c.isMember;
    }
    dispose() {
        this.clearAll();
    }
}
ConversationManager.conversationComparator = (a, b) => b.rankScore - a.rankScore;

export { ConversationManager };
//# sourceMappingURL=ConversationManager.js.map
